import {
  DeleteForever,
  FavoriteTwoTone,
  KeyboardArrowLeft,
  KeyboardArrowRight,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Fab,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  MobileStepper,
  TextFieldProps,
} from "@mui/material";
import React from "react";
import {
  FieldValues,
  Path,
  PathValue,
  useController,
  UseControllerProps,
} from "react-hook-form";
import ImagePicker from "./ImagePicker";
import placeholder from "../images/car_placeholder.png";

export type ImagesInputProps<T extends FieldValues> = UseControllerProps<T> &
  Omit<TextFieldProps, "value" | "defaultValue">;

export function ImagesInput<T extends FieldValues>({
  name,
  control,
  defaultValue,
  rules,
  shouldUnregister,
  label,
  ...props
}: ImagesInputProps<T>) {
  const {
    field: { value, onChange: fieldOnChange, ...field },
    fieldState,
  } = useController<T>({
    name,
    control,
    defaultValue,
    rules,
    shouldUnregister,
  });

  const [activeStep, setActiveStep] = React.useState(0);
  const images: PathValue<T, Path<T>>[] = value || defaultValue || [];

  const handleAddImage = (image: File) => {
    if (image) {
      value.push(URL.createObjectURL(image));
      fieldOnChange(value);
      handleNext();
    }
  };

  const setAsFavorite = () => {
    const imageIndex = activeStep;
    let updatedValue = [...value];
    const favoritePhoto = updatedValue.splice(imageIndex, 1);
    updatedValue = [...favoritePhoto, ...updatedValue];
    fieldOnChange(updatedValue);
    setActiveStep(0);
  };

  const handleRemoveImage = (index: number) => {
    fieldOnChange(value.filter((_, i: number) => i !== index));
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  return (
    <FormControl
      component="fieldset"
      error={fieldState.invalid}
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "#FAFAFA",
      }}
    >
      {label && (
        <FormLabel component="legend" required={props.required}>
          {label}
        </FormLabel>
      )}
      <Box
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
        }}
      >
        <Box
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
            width: "100%",
            paddingLeft: 15,
            paddingRight: 15,
          }}
        >
          {activeStep < images.length ? (
            <>
              <img
                src={images[activeStep]}
                style={{
                  width: "100%",
                  height: undefined,
                  objectFit: "contain",
                  aspectRatio: 4 / 3,
                }}
              />
              <Fab
                aria-label="add image"
                color="secondary"
                component="span"
                onClick={() => handleRemoveImage(activeStep)}
                style={{ position: "absolute", right: 25, top: 10 }}
                size="small"
              >
                <DeleteForever />
              </Fab>
              {activeStep > 0 && (
                <IconButton
                  aria-label="add image"
                  component="span"
                  onClick={setAsFavorite}
                  style={{
                    position: "absolute",
                    bottom: 5,
                    left: "50%",
                    transform: "translate(-50%)",
                  }}
                >
                  <FavoriteTwoTone htmlColor="#ffc31f" />
                </IconButton>
              )}
            </>
          ) : (
            <ImagePicker
              placeholder={placeholder}
              onChange={(image) => handleAddImage(image)}
            />
          )}
        </Box>
        <MobileStepper
          variant="dots"
          steps={images.length + 1}
          position="static"
          activeStep={activeStep}
          sx={{
            maxWidth: 400,
            flexGrow: 1,
            width: "100%",
            backgroundColor: "#FAFAFA",
          }}
          nextButton={
            <Button
              size="small"
              onClick={handleNext}
              disabled={activeStep === images.length}
            >
              <KeyboardArrowRight />
            </Button>
          }
          backButton={
            <Button
              size="small"
              onClick={handleBack}
              disabled={activeStep === 0}
            >
              <KeyboardArrowLeft />
            </Button>
          }
        />
      </Box>
      <FormHelperText>{fieldState.error?.message}</FormHelperText>
    </FormControl>
  );
}

export default ImagesInput;
